import React, { useEffect, useState } from 'react';
import { Menu, X, ShoppingCart, User, LogOut, Package } from 'lucide-react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { navLinks } from '../data/siteData';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import './Navbar.css';

const Navbar = ({ onOpenAuth, onOpenCart }) => {
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [userOpen, setUserOpen]   = useState(false);
  const location   = useLocation();
  const navigate   = useNavigate();
  const { isLoggedIn, user, logout } = useAuth();
  const { count }  = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 18);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); setUserOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle('no-scroll', menuOpen);
    return () => document.body.classList.remove('no-scroll');
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    setUserOpen(false);
    navigate('/');
  };

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`} aria-label="Primary navigation">
      <div className="container nav-inner">
        <Link to="/" className="logo" aria-label="NovaCareLinens home">
          <img
            src="/logo.png"
            alt="NovaCareLinens logo"
            className="brand-logo brand-logo--nav"
          />
        </Link>

        <div className={`nav-panel ${menuOpen ? 'open' : ''}`}>
          <ul className="nav-links">
            {navLinks.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  className={({ isActive }) => (isActive ? 'active' : undefined)}
                  end={link.to === '/'}
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <Link className="nav-cta" to="/contact">
            Request quote
          </Link>
        </div>

        {/* ── Right actions ── */}
        <div className="nav-actions">
          {/* Cart */}
          <button
            className="nav-icon-btn cart-btn"
            onClick={onOpenCart}
            aria-label={`Open cart (${count} items)`}
            id="nav-cart-btn"
          >
            <ShoppingCart size={18} />
            {count > 0 && <span className="nav-cart-badge">{count > 99 ? '99+' : count}</span>}
          </button>

          {/* User */}
          {isLoggedIn ? (
            <div className="nav-user-wrap">
              <button
                className="nav-user-btn"
                onClick={() => setUserOpen(v => !v)}
                aria-label="User menu"
                aria-expanded={userOpen}
                id="nav-user-btn"
              >
                <span className="nav-avatar">
                  {user?.name ? user.name[0].toUpperCase() : user?.email?.[0]?.toUpperCase() || 'U'}
                </span>
              </button>
              {userOpen && (
                <div className="nav-user-dropdown" role="menu">
                  <div className="nav-user-info">
                    <p className="nav-user-name">{user?.name || 'Account'}</p>
                    <p className="nav-user-email">{user?.email}</p>
                  </div>
                  <Link to="/account" className="nav-dropdown-item" onClick={() => setUserOpen(false)}>
                    <Package size={14} /> My Orders
                  </Link>
                  <button className="nav-dropdown-item danger" onClick={handleLogout}>
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="nav-signin-btn" onClick={onOpenAuth} id="nav-signin-btn">
              <User size={15} /> Sign In
            </button>
          )}

          <button
            className="nav-toggle"
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
